import { Badge, Card, Group, SimpleGrid, Text } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { LOCKER_STATE_META, lockerState, useDowntime, useLockersHealth, type LockerState } from '@/api/lockers';
import type { Downtime } from '@/api/types';
import { LockerCard } from '@/components/LockerCard';
import { Page, QueryState } from '@/components/Page';
import { ResponsiveTable, type Column } from '@/components/ResponsiveTable';
import { formatDuration, formatMsk } from '@/lib/format';

const STATE_ORDER: LockerState[] = ['offline', 'hw', 'online'];

const downtimeColumns: Column<Downtime>[] = [
    {
        key: 'locker',
        title: 'Постамат',
        mobile: 'title',
        render: (d) => (
            <Group gap="xs" wrap="nowrap">
                <Text fw={600} inherit>
                    {d.locker_name}
                </Text>
                {d.to === null && (
                    <Badge color="red" size="xs" variant="filled">
                        сейчас
                    </Badge>
                )}
            </Group>
        ),
    },
    { key: 'duration', title: 'Длительность', render: (d) => formatDuration(d.downtime_ms) },
    { key: 'from', title: 'Начало (МСК)', render: (d) => formatMsk(d.from) },
    {
        key: 'to',
        title: 'Конец (МСК)',
        render: (d) => (d.to ? formatMsk(d.to) : <Text c="red" inherit>продолжается</Text>),
    },
];

export function HomeScreen() {
    const navigate = useNavigate();
    const lockers = useLockersHealth();
    const downtime = useDowntime();

    const list = lockers.data ?? [];
    const counts = STATE_ORDER.map((s) => ({ state: s, count: list.filter((l) => lockerState(l) === s).length }));
    // Проблемные — сверху: на телефоне главное видно без прокрутки.
    const problems = list.filter((l) => lockerState(l) !== 'online');

    return (
        <Page title="Сводка">
            <QueryState isLoading={lockers.isLoading} error={lockers.error} onRetry={lockers.refetch}>
                <SimpleGrid cols={3} spacing="sm">
                    {counts.map(({ state, count }) => (
                        <Card key={state} padding="md" onClick={() => navigate('/lockers')} className="ff-clickable">
                            <Text size="xs" c="dimmed" tt="uppercase" fw={600} lh={1.3}>
                                {LOCKER_STATE_META[state].label}
                            </Text>
                            <Text fz={{ base: 28, sm: 34 }} fw={900} c={count > 0 && state !== 'online' ? LOCKER_STATE_META[state].color : undefined}>
                                {count}
                            </Text>
                        </Card>
                    ))}
                </SimpleGrid>

                {problems.length > 0 && (
                    <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
                        {problems.map((l) => (
                            <LockerCard key={l.id} locker={l} onClick={() => navigate(`/lockers/${l.id}`)} />
                        ))}
                    </SimpleGrid>
                )}
            </QueryState>

            <Text fw={800} fz="lg">
                Простои за 30 дней
            </Text>
            <QueryState isLoading={downtime.isLoading} error={downtime.error} onRetry={downtime.refetch}>
                <ResponsiveTable
                    data={downtime.data ?? []}
                    columns={downtimeColumns}
                    rowKey={(d) => `${d.locker_name}-${d.from}`}
                    empty="Простоев не было"
                />
            </QueryState>
        </Page>
    );
}
